import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom-v6'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@clouway/ui'
import { MapPin, Settings, Trash2 } from 'lucide-react'

import { useDateFormatter } from '@/hooks/useDateFormatter'
import { useDeactivateTrackingObject } from '../hooks'
import type { TrackingObject } from '../types'

export function ObjectCard({ customerId, object }: { customerId: string; object: TrackingObject }) {
  const { t } = useTranslation('feature')
  const { t: tCommon } = useTranslation('common')
  const { formatDate } = useDateFormatter()
  const { mutate: deactivate, isLoading } = useDeactivateTrackingObject(customerId)
  const base = `/customers/${customerId}/tracking-objects/${object.id}`

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-sm">{object.lpn}</CardTitle>
          <span className="text-muted-foreground text-sm">{formatDate(object.mountedAt)}</span>
        </div>
        <dl className="text-muted-foreground grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-sm">
          <dt>{t('trackingObjects.list.type')}:</dt>
          <dd className="text-foreground">{t(`trackingObjects.types.${object.type}` as 'trackingObjects.types.iot')}</dd>
          <dt>ID:</dt>
          <dd className="text-foreground">{object.trackingId}</dd>
          <dt>MSISDN:</dt>
          <dd className="text-foreground">{object.msisdn}</dd>
        </dl>
      </CardHeader>
      <CardContent className="flex flex-wrap gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              <Settings className="size-4" />
              {t('trackingObjects.list.config')}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem asChild>
              <Link to={`${base}/config/analogs`}>{t('trackingObjects.list.manualConfig')}</Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to={`${base}/config/template`}>{t('trackingObjects.list.templateConfig')}</Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <Button asChild variant="outline" size="sm">
          <Link to={`${base}/status`}>
            <MapPin className="size-4" />
            {t('trackingObjects.list.status')}
          </Link>
        </Button>

        <Button asChild variant="outline" size="sm">
          <Link to={`${base}/move`}>{t('trackingObjects.list.move')}</Link>
        </Button>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" size="sm" className="ml-auto">
              <Trash2 className="size-4" />
              {t('trackingObjects.list.deactivation')}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t('trackingObjects.list.deactivationMessage')} {object.lpn}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t('trackingObjects.list.deactivationDescription')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{tCommon('ui.button.cancel')}</AlertDialogCancel>
              <AlertDialogAction disabled={isLoading} onClick={() => deactivate(object.id)}>
                {t('trackingObjects.list.deactivation')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  )
}
